<template>
    <div
        v-if="isMounted"
        class="l-ss-c-results"
        :class="{
            'l-ss-c-results-empty': !isLoading && !totalItemsCount && !isEmptyFromFilters,
            'l-ss-c-results-empty-from-filters': !isLoading && !totalItemsCount && isEmptyFromFilters,
            'l-ss-c-results-grouped': !isSinglePageStyle,
            'l-ss-c-results-grid': templateOffer === 'column'
        }">
        <div
            class="l-ss-c-results-filters"
            :class="{ 'l-ss-c-results-filters-position-top': !filterPositionLeft }"
            v-if="!$screen.isMobile && (showFilterTags || showSort)">
            <sort
                v-if="showSort"
                class="l-ss-c-filters-sort"
                ref="sortContainer"
                :sort-by="sortBy"
                :sort-dest="sortDest"
                @sort-change="params => $emit('sort-change', params)"
                @result-sort-click="params => $emit('result-sort-click', params)"/>

            <filter-tags
                v-if="showFilterTags"
                :active-filters="filterValues"
                :filters="filters"
                :symbol="symbol"
                :all-categories-tree="allCategoriesTree"
                @change="$emit('filters-change', $event)"
                @reset-filters="$emit('reset-filters')"/>
        </div>

        <template v-if="errorText">
            <div class="l-ss-c-empty-results">
                <p class="l-ss-c-text-caption">{{ $t('connectionProblemReload') }}</p>
                <p class="l-ss-c-text-caption">
                    <small>
                        <pre>{{ errorText || 'Connection problem' }}</pre>
                    </small>
                </p>
                <button
                    class="l-ss-c-button l-ss-c-button-large"
                    @click="$emit('page-reload')">
                    {{ $t('connectionProblemReloadButton') }}
                </button>
            </div>
        </template>

        <div
            v-else-if="!errorText && hasResults"
            v-loading="loadingByFilters">
            <section
                v-if="hasTopOffers"
                class="l-ss-c-results-section">
                <category-title :name="$t('results.topOffersTitle')"/>

                <offers-wrapper
                    :view="layout"
                    :offer-layout="templateOffer"
                    :offers="topOffers"
                    :offers-type="offersType"
                    :store="store"
                    :search-state="searchState"
                    :adult-permission-confirmed="adultPermissionConfirmed"
                    :breakpoint="currentOffersLayoutBreakpoint"
                    grouped
                    @offer-click="onOfferClick"
                    @add-to-cart-click="params => $emit('add-to-cart-click', params)"
                    @remove-from-cart-click="params => $emit('remove-from-cart-click', params)"
                    @add-to-favourites-click="params => $emit('add-to-favourites-click', params)"
                    @remove-from-favourites-click="params => $emit('remove-from-favourites-click', params)"
                    @click-adult="$emit('click-adult', $event)"/>
            </section>

            <section
                v-if="isSinglePageStyle || !groupCategories || hasTopOffers"
                class="l-ss-c-results-section">
                <category-title
                    v-if="hasTopOffers && groupedOffers && groupedOffers.length"
                    :name="$t('results.extraOffersTitle')"/>

                <category-title
                    v-else-if="singleCategory && isNotSingleTopOffer && singleCategory.hits"
                    :show-icon-back="ind.length > 1"
                    :name="singleCategory.name"
                    :id="singleCategory.id"
                    :hits="singleCategory.hits"
                    @click-icon-back="handleOpenCategory($event, '')"
                    @click-title="handleOpenCategory($event, categoryInArr)"/>

                <offers-wrapper
                    :offers="otherOffers"
                    :offers-type="offersType"
                    :view="layout"
                    :offer-layout="templateOffer"
                    :store="store"
                    :search-state="searchState"
                    :offer-props="{ 'data-page': null }"
                    :adult-permission-confirmed="adultPermissionConfirmed"
                    :breakpoint="currentOffersLayoutBreakpoint"
                    @offer-click="onOfferClick"
                    @add-to-cart-click="params => $emit('add-to-cart-click', params)"
                    @remove-from-cart-click="params => $emit('remove-from-cart-click', params)"
                    @add-to-favourites-click="params => $emit('add-to-favourites-click', params)"
                    @remove-from-favourites-click="params => $emit('remove-from-favourites-click', params)"
                    @click-adult="$emit('click-adult', $event)"/>
            </section>

            <template v-else>
                <section
                    v-for="(group, groupIndex) in slicedGroupedOffers"
                    :key="group.category.id"
                    class="l-ss-c-results-section l-ss-c-results-categories-category"
                    :class="{ 'l-ss-c-results-section-first': groupIndex === 0 }">
                    <category-title
                        :show-icon-back="singleCategory"
                        :name="group.category.name"
                        :id="group.category.id"
                        :hits="group.category.hits"
                        @click-icon-back="handleOpenCategory($event, '')"
                        @click-title="handleOpenCategory($event, group.category)">
                        <template
                            v-if="group.pathCategoryArr && group.pathCategoryArr.length"
                            #path>
                            <span
                                v-for="(item , index) in group.pathCategoryArr.filter(item => item.title)"
                                :key="index"
                                @click="(event) => handleOpenCategory(event, item)"
                                class="l-ss-c-results-categories-category-path"
                                :class="{ 'l-ss-c-results-url-link': item.url }"
                                v-html="item.title">
                            </span>
                        </template>
                    </category-title>

                    <template
                        v-if="group.pages.length > 0">
                        <offers-wrapper
                            v-for="(page) in group.pages"
                            :key="page.length"
                            :view="layout"
                            :offer-layout="templateOffer"
                            :offers="page.offers"
                            :offers-type="offersType"
                            :store="store"
                            :search-state="searchState"
                            :offer-props="{
                                'data-page': page.$page,
                                'data-group-index': groupIndex,
                                'data-offers-in-row': page.offers.length,
                                'data-start-index': getOfferStartIndex(groupIndex),
                                'data-row': groupIndex + 1,
                            }"
                            :breakpoint="currentOffersLayoutBreakpoint"
                            :adult-permission-confirmed="adultPermissionConfirmed"
                            grouped
                            :available-show-more="isAvailableShowMore(group, page)"
                            @offer-click="onOfferClick"
                            @add-to-cart-click="params => $emit('add-to-cart-click', params)"
                            @remove-from-cart-click="params => $emit('remove-from-cart-click', params)"
                            @add-to-favourites-click="params => $emit('add-to-favourites-click', params)"
                            @remove-from-favourites-click="params => $emit('remove-from-favourites-click', params)"
                            @click-adult="$emit('click-adult', $event)">
                            <template
                                #append
                                v-if="isAvailableShowMore(group, page)">
                                <section-results-show-more
                                    :disabled="isLoading"
                                    :category="group.category"
                                    :configuration="$configuration"
                                    @click="handleOpenCategoryWidget($event, group.category)"/>
                            </template>
                        </offers-wrapper>
                    </template>
                </section>

                <section-results-lazy-load-anchor
                    v-if="hasNextGroupedOffers"
                    @intersected="showMoreGroupedOffers"/>
            </template>

            <div
                class="l-ss-c-results-bottom">
                <section-results-lazy-load-anchor
                    v-if="!(loadingNextPage && isLoading) && isSinglePageStyle && hasNextResults"
                    once
                    @intersected="handleOpenNextPage"/>

                <template v-if="hasResults">
                    <signature v-if="!filterPositionLeft && isShowSignature"/>
                    <div
                        v-if="showDots"
                        class="l-ss-c-results-loader">
                        <pulse-dots/>
                    </div>
                </template>
            </div>
        </div>

        <template v-else-if="!isLoading">
            <section-results-empty
                v-if="!isEmptyFromFilters"
                ref="sectionResultsEmpty"
                :search="search"
                :popular-offers="popularOffers"
                :configuration="$configuration"
                @autocomplete-search="$emit('autocomplete-search', $event)"
                @handle-tags-click="handleTagsClick"
                @add-to-cart-click="params => $emit('add-to-cart-click', params)"
                @remove-from-cart-click="params => $emit('remove-from-cart-click', params)"
                @add-to-favourites-click="params => $emit('add-to-favourites-click', params)"
                @remove-from-favourites-click="params => $emit('remove-from-favourites-click', params)"/>

            <section-results-empty-from-filters
                v-else
                ref="sectionResultsEmptyFromFilters"
                :view="layout"
                :offer-layout="templateOffer"
                :popular-offers="popularOffers"
                :breakpoints="$configuration.layout.emptyResultsBreakpoints"
                @reset-filters="$emit('reset-filters', $event)"
                @add-to-cart-click="params => $emit('add-to-cart-click', params)"
                @remove-from-cart-click="params => $emit('remove-from-cart-click', params)"
                @add-to-favourites-click="params => $emit('add-to-favourites-click', params)"
                @remove-from-favourites-click="params => $emit('remove-from-favourites-click', params)"/>
        </template>
    </div>
</template>

<script>
    import declOfNum from '../../helpers/decl-of-num';
    import iconArrowRight from '../../assets/images/icons/arrow-right-results.svg';
    import OffersWrapper from '../offers-wrapper.vue';
    import FilterTags from '@/core/components/common/filters/filter-types/filter-tags/filter-tags.vue';
    import PulseDots from '@/core/components/pulse-dots';
    import Signature from '@/core/components/signature';

    import LoadingDirective from '@/core/directives/loading';

    import priceFormatMixin from '@/core/mixins/price-format-mixin';
    import FoundItemsNumberMixin from '@/core/mixins/found-items-number-mixin';
    import SectionResultsEmpty from '@/core/components/section-results/section-results-empty.vue';
    import SectionResultsLazyLoadAnchor from '@/core/components/section-results/section-results-lazy-load-anchor.vue';
    import CategoryTitle from '@/core/components/category-title';
    import SectionResultsShowMore from '@/core/components/section-results/section-results-show-more';
    import Sort from '@/core/components/sort.vue';
    import { getCurrencyText } from '@/core/helpers/display';
    import getCurrentBreakpoint from '@/core/helpers/get-current-breakpoint';
    import SectionResultsEmptyFromFilters
        from '@/core/components/section-results/section-results-empty-from-filters.vue';

    const LIMIT_BY_CATEGORIES = 5;

    const sortByScore = (left, right) => {
        if (left.score > right.score) {
            return -1;
        }
        if (left.score < right.score) {
            return 1;
        }
        return 0;
    };

    export default {
        components: {
            SectionResultsEmptyFromFilters,
            Sort,
            SectionResultsShowMore,
            CategoryTitle,
            SectionResultsLazyLoadAnchor,
            SectionResultsEmpty,
            OffersWrapper,
            FilterTags,
            PulseDots,
            Signature,
        },
        directives: {
            loading: LoadingDirective,
        },
        mixins: [
            priceFormatMixin,
            FoundItemsNumberMixin,
        ],
        props: {
            search: {
                type: Object,
                required: false,
                default: () => ({}),
            },
            searchState: {
                type: Object,
            },
            store: {
                type: Object,
            },
            offerTemplates: {
                type: Object,
                required: false,
            },
            skip: {
                type: [String, Number],
                required: true,
            },
            limit: {
                type: [String, Number],
                required: true,
            },
            filterValues: {
                type: Array,
            },
            filters: {
                type: Array,
            },
            categoryId: {
                type: [String, Number, Array],
                required: true,
            },
            isLoading: {
                type: [String, Boolean],
                default: false,
            },
            loadingNextPage: {
                type: Boolean,
                default: false,
            },
            errorText: {
                type: [String],
                default: '',
            },
            hasResults: {
                type: [String, Boolean],
                default: false,
            },
            hasQuery: {
                type: [String, Boolean],
                default: false,
            },
            hasLoadedOnce: {
                type: [String, Boolean],
                default: false,
            },
            hasFilters: {
                type: [String, Boolean],
                default: false,
            },
            searchRequestId: {
                type: String,
                required: true,
            },
            groupCategories: {
                type: Boolean,
                required: true,
            },
            categories: {
                type: [String, Number, Array],
                required: true,
            },
            totalItemsCount: {
                type: Number,
                required: true,
            },
            filterPositionLeft: {
                type: Boolean,
                default: false,
            },
            query: {
                type: [String],
                default: '',
            },
            adultPermissionConfirmed: {
                type: Boolean,
                default: false,
            },
            isShowSignature: {
                type: Boolean,
                default: false,
            },
            showSorting: {
                type: Boolean,
                default: true,
            },
            sortBy: {
                type: String,
                default: '',
            },
            sortDest: {
                type: String,
                default: '',
            },
            currency: {
                type: String,
                default: '',
            },
            loadingByFilters: {
                type: Boolean,
                default: false,
            },
            allCategoriesTree: {
                type: Array,
            },
        },
        data() {
            return {
                currentSlideIndex: 0,
                currentCarouselStatus: 0,
                isMounted: false,
                categoryItem: {},
                pathCategory: [],
                ind: [],
                queryChanger: '',
                currentLimitByCategories: LIMIT_BY_CATEGORIES,
                windowWidth: 0,
                isFirstShow: true,
            // SectionFilters: null,
            };
        },
        computed: {
            taps() {
                return {
                    desktop: (this.$configuration.offer?.taps?.desktop) ? this.$configuration.offer?.taps?.desktop : {},
                    mobile: (this.$configuration.offer?.taps?.mobile) ? this.$configuration.offer?.taps?.mobile : {},
                };
            },
            templateOffer() {
                return this.$screen.isMobile ? 'row' : this.$configuration.layout.templateOffer === 'grid' ? 'column' : 'row';
            },
            layout() {
                return this.$screen.isMobile ? 'row' : this.$configuration.layout.templateOffer;
            },
            showSort() {
                return this.$configuration.isSortVisible && this.filterPositionLeft && this.showSorting;
            },
            categoryInArr() {
                return (Array.isArray(this.groupedOffers)) ? this.groupedOffers[0].category : '';
            },
            iconArrowRight() {
                return this.$configuration.customSvgIconRightArrow ? this.$configuration.customSvgIconRightArrow : iconArrowRight;
            },
            showDots() {
                return this.loadingNextPage && this.isLoading;
            },
            hasCachedResults() {
                return this.search && this.search.offers && this.search.offers.length;
            },
            activeCategories() {
                return this.categoryId ?
                    [this.categoryId].flat().map(categoryId => this.getCategoryById(categoryId)) :
                    [];
            },
            singleCategory() {
                return this.activeCategories.length === 1 ? this.activeCategories[0] : null;
            },
            isEmptyFromFilters() {
                if (!this.hasResults && !this.isLoading) {
                    return this.hasFilters;
                }

                return false;
            },
            hasActiveCategories() {
                return this.activeCategories && this.activeCategories.length > 0;
            },
            isSinglePageStyle() {
                return Boolean(this.hasActiveCategories || !this.groupCategories);
            },
            showFilterTags() {
                return !this.$screen.isMobile && this.filterValues.length;
            },
            offers() {
                const roundUpPrice = (price) => {
                    if (price && Number.isInteger(price)) {
                        return price;
                    } else if (price) {
                        return this.getRoundedPrice(price);
                    } else {
                        return null;
                    }
                };
                return this.search && this.search.offers ? this.search.offers.map(offer => {
                    return {
                        ...offer,
                        price: roundUpPrice(+offer.price),
                        oldPrice: (roundUpPrice(+offer.oldPrice) !== roundUpPrice(+offer.price)) ?
                            roundUpPrice(+offer.oldPrice) :
                            null,
                    };
                }) : [];
            },
            topOffersIds() {
                return this.search && this.search.topOffersIds ? this.search.topOffersIds : [];
            },
            hasTopOffers() {
                return !!this.topOffersIds.length;
            },
            topOffers() {
                return this.topOffersIds.map(offerId => this.offers.find(item => item.id === offerId)).filter(item => !!item).sort(sortByScore);
            },
            offersType() {
                return this.search?.type;
            },
            otherOffers() {
                return this.offers.filter(item => !this.topOffersIds.includes(item.id));
            },
            groupedOffers() {
                const grouped = this.otherOffers.reduce((memo, offer, index) => {
                    memo[offer.category.id] = memo[offer.category.id] || {
                        category: this.getCategoryById(offer.category.id) || offer.category,
                        offers: [],
                        pages: {},
                        sortIndex: index,
                        startPage: null,
                        categoryItem: [],
                    };
                    memo[offer.category.id].offers.push(offer);
                    memo[offer.category.id].categoryItem = (this.categories) ?
                        this.categories.find(category => +category.id === +memo[offer.category.id].category.id) :
                        {};

                    memo[offer.category.id].pathCategory = function (categories, item = {}) {
                        memo[offer.category.id].pathCategoryArr = [];
                        if (item.parentId) {
                            memo[offer.category.id].categoryItem = categories.find(category => +category.id === +item.parentId);
                            memo[offer.category.id].pathCategoryArr.push(memo[offer.category.id].categoryItem);
                            if (memo[offer.category.id].categoryItem.parentId) {
                                memo[offer.category.id].pathCategory(
                                    categories,
                                    memo[offer.category.id].categoryItem);
                            }
                        }
                        return memo[offer.category.id].pathCategoryArr;
                    };

                    memo[offer.category.id].pathCategory(this.categories, memo[offer.category.id].categoryItem);

                    const page = offer.$page === undefined ? null : offer.$page;

                    if (page !== null &&
                        (memo[offer.category.id].startPage === null || memo[offer.category.id].startPage > page)) {
                        memo[offer.category.id].startPage = page;
                    }

                    memo[offer.category.id].pages[page] = memo[offer.category.id].pages[page] || {
                        $page: page,
                        $pageLast: false,
                        offers: [],
                    };
                    memo[offer.category.id].pages[page].offers.push(offer);

                    if (offer.$pageLast) {
                        memo[offer.category.id].pages[page].$pageLast = true;
                    }
                    return memo;
                }, {});

                Object.keys(grouped).forEach(key => {
                    grouped[key].pages = Object.values(grouped[key].pages).sort((left, right) => {
                        if (left.$page > right.$page) return 1;
                        else if (left.$page < right.$page) return -1;
                        else return 0;
                    });
                });

                return Object.values(grouped).sort((a, b) => a.sortIndex - b.sortIndex);
            },
            availableScrollToOffer() {
                return this.store.state.availableScrollToOffer;
            },
            slicedGroupedOffers() {
                return !this.availableScrollToOffer ? this.groupedOffers.slice(0, this.currentLimitByCategories) : this.groupedOffers;
            },
            hasNextGroupedOffers() {
                return this.currentLimitByCategories < this.groupedOffers.length;
            },
            hasPrevResults() {
                return this.skip > 0;
            },
            hasNextResults() {
                if (!this.hasResults) {
                    return false;
                }

                return this.search.hits !== this.search.offers.length;
            },
            nextBottomCount() {
                return this.getNextPageCount(this.skip, this.limit);
            },
            isNotSingleTopOffer() {
                return !this.topOffers.length || this.topOffers.length && this.topOffers.length !== this.offers.length;
            },
            popularOffers() {
                if (window['searchbooster']) {
                    return window['searchbooster'].api?.controller?.popularOffers || [];
                }
                return [];
            },
            hasOffersLayoutBreakpoints() {
                return !!this.$configuration.layout.resultsBreakpoints;
            },
            currentOffersLayoutBreakpoint() {
                const layout = this.$configuration.layout;

                const defaultBreakpoint = {
                    columnCount: layout.gridColumnCount,
                    gridGap: layout.gridGap,
                };

                if (!this.hasOffersLayoutBreakpoints) {
                    return defaultBreakpoint;
                }

                let currentBreakpointColumnCount = getCurrentBreakpoint(layout.resultsBreakpoints, this.windowWidth);

                /*
                 * For (let breakpoint in layout.resultsBreakpoints) {
                 *     if (this.windowWidth >= breakpoint) {
                 *         currentBreakpointColumnCount = layout.resultsBreakpoints[breakpoint];
                 *     }
                 * }
                 */

                if (currentBreakpointColumnCount && !currentBreakpointColumnCount.gridGap) {
                    currentBreakpointColumnCount.gridGap = layout.gridGap;
                }

                return currentBreakpointColumnCount || defaultBreakpoint;
            },
            symbol() {
                return this.$configuration.offer.currency || getCurrencyText(this.currency);
            },
        },
        watch: {
            groupedOffers() {
                this.queryChanger = this.query;
            },
        },
        beforeDestroy() {
            window.removeEventListener('resize', this.calcWindowWidth);
        },
        mounted() {
            if (this.isSinglePageStyle || !this.groupCategories) {
                this.categoryItem = this.categories.find(category => +category.id === +this.categoryId);
                let pathCategory = (categories, item = {}) => {
                    let categoryItem = {};
                    if (item.parentId) {
                        categoryItem = categories.find(category => +category.id === +item.parentId);
                        this.pathCategory.push(categoryItem);
                        if (categoryItem.parentId) pathCategory(categories, categoryItem);
                    }
                    return this.pathCategory;
                };
                this.pathCategory = pathCategory(this.categories, this.categoryItem);
                this.$set(this.pathCategory, this.pathCategory.indexOf(this.categoryItem), this.categoryItem);
            }

            this.$nextTick(() => {
                this.isMounted = true;
            });

            if (this.$configuration.customScript) {
                eval(this.$configuration.customScript);
            }

            if (this.templateOffer === 'column' && this.hasOffersLayoutBreakpoints) {
                this.calcWindowWidth();
                window.addEventListener('resize', this.calcWindowWidth);
            }
        },
        methods: {
            getNextPageCount(skip, limit) {
                skip = window['searchbooster'].api?.controller.skip ? window['searchbooster'].api?.controller.skip : skip;
                const hits = this.search.hits;
                const active = Number(skip) + Number(limit);
                if (active < (hits - limit)) {
                    return this.limit;
                }
                let value = hits - active;
                return value > 0 ? value : 0;
            },
            getCategoryById(categoryId) {
                if (categoryId && this.search && this.search.categories) {
                    return this.search.categories.find(category => category.id.toString() === categoryId.toString());
                }
                return null;
            },
            getOffersByCategoryId(categoryId) {
                const group = this.groupedOffers.find(group => group.category.id.toString() === categoryId.toString());
                return group ? group.offers : [];
            },
            hasNextCategoryItems(categoryId) {
                const offers = this.getOffersByCategoryId(categoryId);
                const category = this.getCategoryById(categoryId);
                if (!category) {
                    return false;
                }
                return category && offers.length < category.hits;
            },
            handleOpenCategory(event, category) {
                event.preventDefault();
                event.stopPropagation();
                let categoryId = this.singleCategory ? this.singleCategory.id : category.id;
                let categoryItem = this.categories.find(category => category.id === categoryId);
                if (categoryItem && !category.url) {
                    category.url = categoryItem.url;
                }
                if (category.url) {
                    this.handleResultCategoryClick(event, category);
                    window.open(category.url, this.$configuration.linkTargetBlank ? '_blank' : '_self');
                } else {
                    this.$emit('navigate-category', { categoryId, skip: 0 });

                    this.handleResultCategoryClick(event, category);
                }
            },
            handleResultCategoryClick(event, category) {
                const { hits, id, name } = category;

                const type = this.$root.$refs.inner.typeWidget === 'image-search-similar' ? 'iwResultCategoryClick' : 'resultCategoryClick';

                this.$emit('result-category-click', { type: type, hits, id, name, event });
            },
            handleOpenCategoryWidget(event, category) {
                event.preventDefault();
                event.stopPropagation();
                let categoryId = this.singleCategory ? this.singleCategory.id : category.id;
                const { hits, id, name } = category;
                this.$emit('view-in-category-click', { type: 'viewInCategoryClick', event, hits, id, name });
                this.$emit('navigate-category', { categoryId, skip: 0 });
            },
            handleOpenPrevPage(event) { // Нигде не используется. Возможно надо удалить.
                event.preventDefault();
                event.stopPropagation();
                this.$emit('navigate-page-prev');
                let tag = document.getElementsByTagName('searchbooster-search-popup');
                let popup = tag[0].shadowRoot.querySelector('.l-ss-c-search-popup-wrapper-mask');
                popup.scrollTop = 0;
            },
            handleOpenNextPage() {
                /*
                 * Event.preventDefault();
                 * event.stopPropagation();
                 */
                this.$emit('navigate-page-next');
            },
            isAvailableShowMore(group, page) {
                return !this.$screen.isMobile &&
                    page.offers.length === this.$configuration.searchQuery.groupByCategoriesSize &&
                    group.category.hits > page.offers.length;
            },
            showMoreGroupedOffers() {
                this.currentLimitByCategories += LIMIT_BY_CATEGORIES;
            },
            compileCategoryHitsName(hits) {
                return declOfNum(hits, [
                    this.$t('results.seeMore1'),
                    this.$t('results.seeMore2'),
                    this.$t('results.seeMore3'),
                ]);
            },
            onOfferClick(data) {
                if (this.isSinglePageStyle) {
                    const limit = this.$configuration.searchQuery.limit;
                    data.page = Math.floor(data.pos / limit) + 1;
                } else {
                    data.page = 1;
                }

                this.$emit('offer-click', data);
            },
            getOfferStartIndex(groupIndex) {
                return this.groupedOffers.slice(0, groupIndex).reduce((accumulator, currentValue, index) => {
                    if (groupIndex !== index) {
                        return accumulator + currentValue.offers.length;
                    }
                }, 0);
            },
            handleTagsClick(tag) {
                this.$emit('handle-tags-click', tag);
                this.$emit('tags-click', { tag, fromEmpty: true });
            },
            calcWindowWidth() {
                this.windowWidth = window.innerWidth;
            },
        },
    };
</script>
